import { useEffect, useRef, useState } from 'react';

// Inline-editable text cell, moved out of pages/Operations.jsx (2026-09-14) so
// the Project Tracker's PO # column saves through the same control the
// Operations Active Jobs table uses. Behavior is unchanged from the Ops copy.
//
// Inline-editable job number cell for the Active Jobs table. Holds local
// state so the user can type freely without triggering re-renders, saves on
// blur or Enter when the value actually changed, and reverts to the original
// value on Esc or on save failure (e.g. unique-violation 409).
// width 148 (was 110): the lap-form TA job numbers this app now mints are up
// to 17 chars ("810-2-26-VAL-SRTX") and rendered visibly truncated.
// onGenerate (optional, 2026-08-07): when the cell is EMPTY, a "＋#" button
// mints the next TA pool number instead of making ops go look one up. Only
// wired on Won/Active/Complete sections; a bid must not burn a pool slot.
// The full register lives on the Job #s tab.
export function JobNumberCell({ value, disabled, onSave, onGenerate, maxLength = 40, width = 148, subtle = false }) {
  const original = value || '';
  const [v, setV] = useState(original);
  const [busy, setBusy] = useState(false);
  // Sync local state when the row's value changes from the server (e.g. after
  // a successful save reload). Cheap because key prop on each row already
  // remounts on id change; this catches in-place updates.
  useEffect(() => { setV(original); }, [original]);
  // Esc must cancel, not save. blur() fires focusout synchronously inside the
  // keydown, so onBlur runs with the TYPED value still in its closure before
  // setV(original) lands; the ref tells that one blur to do nothing.
  const cancelRef = useRef(false);
  const commit = async () => {
    if (cancelRef.current) { cancelRef.current = false; return; }
    if (busy) return;
    if (v === original) return;
    setBusy(true);
    try { await onSave(v); }
    catch { setV(original); } // server rejected (likely unique violation)
    finally { setBusy(false); }
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelRef.current = true; setV(original); e.target.blur(); }
  };
  const generate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const jn = await onGenerate();
      if (jn) setV(jn);
    } catch { /* parent surfaces the error in the page banner */ }
    finally { setBusy(false); }
  };
  const showMint = !!onGenerate && !v.trim();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <input
        type="text"
        value={v}
        placeholder="—"
        maxLength={maxLength}
        disabled={disabled || busy}
        onChange={e => setV(e.target.value)}
        onFocus={() => { cancelRef.current = false; }}
        onBlur={commit}
        onKeyDown={onKeyDown}
        style={{
          width: showMint ? width - 30 : width,
          padding: '4px 6px', fontSize: 12, fontFamily: 'monospace',
          border: '1px solid #D1D5DB', borderRadius: 4,
          background: busy ? '#F3F4F6' : '#fff',
          color: v ? (subtle ? '#374151' : '#E36F2C') : '#9CA3AF',
          fontWeight: v && !subtle ? 700 : 400,
        }}
        title="Click to edit. Press Enter or click away to save. Esc to cancel."
      />
      {showMint && (
        <button
          type="button"
          onClick={generate}
          disabled={disabled || busy}
          title="Assign the next open TA job number (801-820, this job's year). See the Job #s tab for the whole pool."
          style={{
            padding: '4px 5px', fontSize: 11, lineHeight: 1, cursor: (disabled || busy) ? 'default' : 'pointer',
            border: '1px solid #D1D5DB', borderRadius: 4, background: '#F9FAFB',
            color: '#E36F2C', fontWeight: 700, opacity: (disabled || busy) ? 0.5 : 1,
          }}>
          {busy ? '…' : '＋#'}
        </button>
      )}
    </span>
  );
}

// The generic name for non-job-number uses (a PO # cell, say). Same component.
export const InlineTextCell = JobNumberCell;
